import { useEffect, useState } from "react";
import {
  ORDERS_API,
  PRODUCTS_API,
  PRODUCTSIZE_API,
} from "../../../../Utilities/APIs/APIs";
import useAxiosInstance from "../../../../Utilities/Hooks/useAxiosInstance";

function AddProductToOrder({
  orderID,
  setShowAddProductToOrder,
  triggerFetch,
  order,
}) {
  const axiosInstance = useAxiosInstance();

  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [sizes, setSizes] = useState([]);

  const [selectedSizeId, setSelectedSizeId] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [orderItems, setOrderItems] = useState([]);

  const [latestExchangedItem, setLatestExchangedItem] =
    useState(null);

  /*
  =========================================
  DELIVERY
  =========================================
  */

  const [addExchangeDelivery, setAddExchangeDelivery] =
    useState(false);

  const [exchangeDeliveryType, setExchangeDeliveryType] =
    useState("normal");

  const [deliveryCharge, setDeliveryCharge] =
    useState(0);

  /*
  =========================================
  SHIPPING / DISTRICT
  =========================================
  */

  const shippingMethod = String(
    order?.shippingMethod || ""
  )
    .toLowerCase()
    .trim();

  const customerDistrict = (
    typeof order?.customerDistrict === "object"
      ? order?.customerDistrict?.name
      : order?.customerDistrict || ""
  )
    .toString()
    .toLowerCase()
    .trim();

  const isDhaka =
    customerDistrict.includes("dhaka");

  const isPickup =
    shippingMethod.includes("pickup") ||
    shippingMethod.includes("in-store") ||
    shippingMethod.includes("instore");

  /*
  =========================================
  FETCH ORDER ITEMS
  =========================================
  */

  useEffect(() => {
    async function fetchOrderItems() {
      try {
        const { data } = await axiosInstance.get(
          ORDERS_API + "orderItemsFromOrder/" + orderID
        );

        setOrderItems(data?.orderItems || []);

        const exchangedItems = (
          data?.orderItems || []
        )
          .filter(
            (item) =>
              item?.itemStatus === "exchanged"
          )
          .sort(
            (a, b) =>
              new Date(b.updatedAt) -
              new Date(a.updatedAt)
          );

        if (exchangedItems.length > 0) {
          setLatestExchangedItem(
            exchangedItems[0]
          );
        } else {
          setLatestExchangedItem(null);
        }
      } catch (err) {
        console.error(err);
      }
    }

    if (orderID) {
      fetchOrderItems();
    }
  }, [orderID, axiosInstance]);

  /*
  =========================================
  PRODUCT SEARCH
  =========================================
  */

  useEffect(() => {
    async function fetchProducts() {
      if (!search.trim()) {
        setProducts([]);
        return;
      }

      try {
        const { data } = await axiosInstance.get(
          `${PRODUCTS_API}?search=${search}`
        );

        const list = Array.isArray(data)
          ? data
          : data?.products || [];

        setProducts(list);
      } catch (err) {
        console.error(err);
        setProducts([]);
      }
    }

    const timer = setTimeout(fetchProducts, 400);

    return () => clearTimeout(timer);
  }, [search, axiosInstance]);

  /*
  =========================================
  SELECT PRODUCT
  =========================================
  */

  async function handleSelectProduct(product) {
    setSelectedProduct(product);

    setProducts([]);

    setSearch(
      `${product?.name || ""} ${
        product?.squId || ""
      }`
    );

    setSelectedSizeId("");

    setSizes([]);

    setUnitPrice(
      product?.currentPrice ||
        product?.price ||
        ""
    );

    try {
      const res = await axiosInstance.get(
        PRODUCTSIZE_API +
          "getProductSizesByProduct/" +
          product._id
      );

      setSizes(res?.data || []);
    } catch (err) {
      console.error(err);

      setMsg(
        "Could not load sizes for this product."
      );
    }
  }

  /*
  =========================================
  EXCHANGE CALCULATIONS
  =========================================
  */

const selectedProductTotal =
  Number(unitPrice || 0) * Number(quantity || 0);

const exchangedItemsTotal = orderItems
  .filter((item) => item?.itemStatus === "exchanged")
  .reduce((acc, item) => {
    return (
      acc +
      Number(item?.unitPrice || 0) *
        Number(item?.quantity || 1)
    );
  }, 0);

const exchangePreviousAmount = exchangedItemsTotal;

const exchangeDifference =
  latestExchangedItem
    ? selectedProductTotal - exchangedItemsTotal
    : 0;

const finalExchangeAmount =
  exchangeDifference + deliveryCharge;

  /*
  =========================================
  DELIVERY LOGIC
  =========================================
  */

  useEffect(() => {
    if (!latestExchangedItem) {
      setDeliveryCharge(0);
      return;
    }

    if (!addExchangeDelivery) {
      setDeliveryCharge(0);
      return;
    }

    if (isPickup) {
      setDeliveryCharge(0);
      return;
    }

    if (isDhaka) {
      setDeliveryCharge(
        exchangeDeliveryType === "express"
          ? 200
          : 70
      );
    } else {
      setDeliveryCharge(120);
    }
  }, [
    latestExchangedItem,
    addExchangeDelivery,
    exchangeDeliveryType,
    isDhaka,
    isPickup,
  ]);

  /*
  =========================================
  FINAL EXCHANGE
  =========================================
  */

  /*
  =========================================
  SUBMIT
  =========================================
  */

  async function handleSubmit(e) {
    e.preventDefault();

    setMsg("");

    if (!selectedProduct?._id) {
      setMsg("Please select a product.");
      return;
    }

    if (!selectedSizeId) {
      setMsg("Please select a size.");
      return;
    }

    if (
      !quantity ||
      Number(quantity) <= 0
    ) {
      setMsg(
        "Please enter valid quantity."
      );
      return;
    }

    try {
      setLoading(true);

      await axiosInstance.post(
        `${ORDERS_API}addProductToExistingOrder/${orderID}`,
        {
          productId: selectedProduct._id,
          sizeId: selectedSizeId,
          quantity: Number(quantity),
          unitPrice: Number(unitPrice),
          addExchangeDelivery,
          exchangeDeliveryType,
        }
      );

      triggerFetch();

      setShowAddProductToOrder(false);
    } catch (err) {
      console.error(err);

      setMsg(
        err?.response?.data?.message ||
          "Failed to add product."
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <form onSubmit={handleSubmit}>
        {/* SEARCH */}

        <div style={{ marginBottom: 10 }}>
          <label
            style={{
              color: "#fff",
              fontSize: 13,
            }}
          >
            Search Product by Name / SKU
          </label>

          <input
            className="input_field"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);

              setSelectedProduct(null);

              setSelectedSizeId("");

              setSizes([]);
            }}
            placeholder="Search product..."
            style={{ marginTop: 6 }}
          />

          {products.length > 0 && (
            <div
              style={{
                marginTop: 6,
                background: "#111827",
                border:
                  "1px solid #374151",
                borderRadius: 4,
                maxHeight: 180,
                overflowY: "auto",
              }}
            >
              {products.map((product) => (
                <button
                  key={product._id}
                  type="button"
                  onClick={() =>
                    handleSelectProduct(
                      product
                    )
                  }
                  style={{
                    display: "block",
                    width: "100%",
                    textAlign: "left",
                    padding: "8px",
                    color: "#fff",
                    background: "transparent",
                    border: "none",
                    borderBottom:
                      "1px solid #374151",
                    cursor: "pointer",
                  }}
                >
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {product.name}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: "#9ca3af",
                    }}
                  >
                    SKU: {product.squId} |
                    Tk{" "}
                    {product.currentPrice ||
                      product.price}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* EXCHANGE */}

        {latestExchangedItem && (
          <div
            style={{
              background: "#fff7ed",
              border: "1px solid #fdba74",
              padding: "10px",
              borderRadius: "6px",
              marginBottom: "12px",
              fontSize: "12px",
              lineHeight: 1.7,
              color: "#9a3412",
            }}
          >
            <div
              style={{
                fontWeight: "700",
                marginBottom: "6px",
              }}
            >
              Exchange Detected
            </div>

            {/* <div>
              Previous Product:
              {" "}
              {
                latestExchangedItem?.item
                  ?.name
              }
            </div> */}

            <div>
              Previous Exchanged Amount:
              {" "}
              Tk{" "}
              {exchangePreviousAmount}
            </div>
          </div>
        )}

        {/* PRODUCT */}

        {selectedProduct && (
          <div
            style={{
              fontSize: 12,
              color: "#fff",
              marginBottom: 12,
              lineHeight: 1.7,
            }}
          >
            <div>
              <strong>Product:</strong>{" "}
              {selectedProduct.name}
            </div>

            <div>
              <strong>SKU:</strong>{" "}
              {selectedProduct.squId}
            </div>

            <div>
              <strong>Price:</strong>{" "}
              {
                selectedProduct.currentPrice
              }{" "}
              TK
            </div>
          </div>
        )}

        {/* SIZE */}

        <div style={{ marginBottom: 10 }}>
          <label
            style={{
              color: "#fff",
              fontSize: 13,
            }}
          >
            Select Size
          </label>

          <select
            className="input_field"
            value={selectedSizeId}
            onChange={(e) =>
              setSelectedSizeId(
                e.target.value
              )
            }
            disabled={!selectedProduct}
            style={{ marginTop: 6 }}
          >
            <option value="">
              Select Size
            </option>

            {sizes.map((ps) => {
              const sizeId =
                ps?.size?._id ||
                ps?.size ||
                ps?._id;

              const sizeName =
                ps?.size?.name ||
                "Size";

              const availableQty =
                Number(
                  ps?.quantity || 0
                );

              return (
                <option
                  key={ps._id}
                  value={sizeId}
                  disabled={
                    availableQty <= 0
                  }
                >
                  {sizeName} | Available:
                  {" "}
                  {availableQty}
                </option>
              );
            })}
          </select>
        </div>

        {/* QUANTITY */}

        <div style={{ marginBottom: 10 }}>
          <label
            style={{
              color: "#fff",
              fontSize: 13,
            }}
          >
            Quantity
          </label>

          <input
            className="input_field"
            type="number"
            min="1"
            value={quantity}
            onChange={(e) =>
              setQuantity(e.target.value)
            }
            style={{ marginTop: 6 }}
          />
        </div>

        {/* DELIVERY OPTIONS */}

        {latestExchangedItem &&
          !isPickup && (
            <div
              style={{
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  color: "#fff",
                  fontSize: 13,
                  marginBottom: 8,
                  fontWeight: "600",
                }}
              >
                Exchange Delivery
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                }}
              >
                <button
                  type="button"
                  onClick={() =>
                    setAddExchangeDelivery(
                      false
                    )
                  }
                  style={{
                    padding: "8px 12px",
                    background:
                      !addExchangeDelivery
                        ? "#f1652f"
                        : "transparent",
                    border:
                      "1px solid #f1652f",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Free Delivery
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setAddExchangeDelivery(
                      true
                    )
                  }
                  style={{
                    padding: "8px 12px",
                    background:
                      addExchangeDelivery
                        ? "#f1652f"
                        : "transparent",
                    border:
                      "1px solid #f1652f",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  Add Delivery Fee
                </button>
              </div>

              {addExchangeDelivery &&
                isDhaka && (
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      gap: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExchangeDeliveryType(
                          "normal"
                        )
                      }
                      style={{
                        padding:
                          "8px 12px",
                        background:
                          exchangeDeliveryType ===
                          "normal"
                            ? "#f1652f"
                            : "transparent",
                        border:
                          "1px solid #f1652f",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Normal (70)
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setExchangeDeliveryType(
                          "express"
                        )
                      }
                      style={{
                        padding:
                          "8px 12px",
                        background:
                          exchangeDeliveryType ===
                          "express"
                            ? "#f1652f"
                            : "transparent",
                        border:
                          "1px solid #f1652f",
                        color: "#fff",
                        cursor: "pointer",
                      }}
                    >
                      Express (200)
                    </button>
                  </div>
                )}

              {addExchangeDelivery &&
                !isDhaka && (
                  <div
                    style={{
                      marginTop: 10,
                      color: "#9ca3af",
                      fontSize: 12,
                    }}
                  >
                    Outside Dhaka Delivery:
                    Tk 120
                  </div>
                )}
            </div>
          )}

        {/* SUMMARY */}

        {latestExchangedItem &&
          selectedProduct && (
            <div
              style={{
                background: "#111827",
                border: "1px solid #374151",
                padding: "14px",
                borderRadius: "6px",
                marginBottom: "12px",
                color: "#fff",
                fontSize: "12px",
                lineHeight: 1.9,
              }}
            >
              <div
                style={{
                  fontWeight: "700",
                  marginBottom: "10px",
                  color: "#f3f4f6",
                  borderBottom: "1px dashed #374151",
                  paddingBottom: "8px",
                }}
              >
                Exchange Summary
              </div>

              {/* EXCHANGED PRODUCTS */}

              <div
                style={{
                  color: "#d1d5db",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    fontWeight: "600",
                    marginBottom: "4px",
                    color: "#f9fafb",
                  }}
                >
                  Exchanged Products:
                </div>

                {orderItems
                  .filter(
                    (item) =>
                      item?.itemStatus === "exchanged"
                  )
                  .map((item) => (
                    <div key={item?._id}>
                      • {item?.item?.name} — Tk{" "}
                      {Number(
                        item?.unitPrice || 0
                      ) *
                        Number(
                          item?.quantity || 1
                        )}
                    </div>
                  ))}
              </div>

              {/* CREDIT */}

              <div
                style={{
                  marginTop: "8px",
                }}
              >
                Exchange Credit:
                <span
                  style={{
                    color: "#4ade80",
                    marginLeft: "6px",
                    fontWeight: "700",
                  }}
                >
                  Tk {exchangePreviousAmount}
                </span>
              </div>

              {/* NEW PRODUCT */}

              <div
                style={{
                  marginTop: "8px",
                }}
              >
                New Product Total:
                <span
                  style={{
                    marginLeft: "6px",
                  }}
                >
                  Tk {selectedProductTotal}
                </span>
              </div>

              {/* DIFFERENCE */}

              <div
                style={{
                  marginTop: "8px",
                }}
              >
                Difference:
                <span
                  style={{
                    color:
                      exchangeDifference >= 0
                        ? "#fbbf24"
                        : "#fca5a5",
                    marginLeft: "6px",
                    fontWeight: "700",
                  }}
                >
                  {exchangeDifference > 0
                    ? "+"
                    : ""}
                  {exchangeDifference}
                </span>
              </div>

              {/* DELIVERY */}

              <div
                style={{
                  marginTop: "8px",
                }}
              >
                Delivery:
                <span
                  style={{
                    marginLeft: "6px",
                  }}
                >
                  Tk {deliveryCharge}
                </span>
              </div>

              {/* FINAL */}

              <div
                style={{
                  marginTop: "12px",
                  paddingTop: "10px",
                  borderTop: "1px dashed #374151",
                  fontWeight: "700",
                  color:
                    finalExchangeAmount >= 0
                      ? "#4ade80"
                      : "#fca5a5",
                  fontSize: "13px",
                }}
              >
                {finalExchangeAmount >= 0
                  ? `Customer Will Pay: Tk ${finalExchangeAmount}`
                  : `Customer Refund: Tk ${Math.abs(
                      finalExchangeAmount
                    )}`}
              </div>
            </div>
          )}

        {/* ERROR */}

        {msg && (
          <p
            style={{
              color: "#ffb74d",
              fontSize: 13,
              marginBottom: 8,
            }}
          >
            {msg}
          </p>
        )}

        {/* BUTTONS */}

        <div className="delete_btn">
          <button
            type="submit"
            className="delete"
            disabled={loading}
          >
            {loading
              ? "Adding..."
              : latestExchangedItem
              ? "Add Exchange Product"
              : "Add Product"}
          </button>

          <button
            type="button"
            className="cancel"
            onClick={() =>
              setShowAddProductToOrder(
                false
              )
            }
          >
            Close
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddProductToOrder;